import { accessSync, constants, mkdtempSync, readdirSync, rmSync } from 'fs';
import { tmpdir } from 'os';
import { extname, join, resolve } from 'path';
import { spawnSync } from 'child_process';
import { OkraRuntimeError } from '../errors.js';
import type { LocalDoctorReport, LocalToolAvailability } from './types.js';
import { Data, Effect } from 'effect';

export class LocalExtractError extends Data.TaggedError('LocalExtractError')<{
  readonly method: 'localToolReport' | 'extractLocalDocument';
  readonly status?: number;
  readonly cause: unknown;
}> {}

function localExtractBoundary<A>(
  method: LocalExtractError['method'],
  run: () => A,
): Effect.Effect<A, LocalExtractError> {
  return Effect.try({
    try: run,
    catch: (cause) =>
      new LocalExtractError({
        method,
        ...(cause instanceof OkraRuntimeError ? { status: cause.status } : {}),
        cause,
      }),
  });
}

function runLocalExtractEffect<A>(program: Effect.Effect<A, LocalExtractError>): A {
  return Effect.runSync(program.pipe(Effect.catch((error) => Effect.fail(error.cause))));
}

const TOOL_NAMES = ['pdftotext', 'pdfinfo', 'pdftoppm', 'tesseract'] as const;

type ToolName = (typeof TOOL_NAMES)[number];

interface ExtractedPage {
  pageNumber: number;
  text: string;
  ocrApplied: boolean;
}

export interface ExtractedDocument {
  pageCount: number;
  charCount: number;
  ocrUsed: boolean;
  pages: ExtractedPage[];
  tools: LocalDoctorReport['tools'];
}

function runCommand(command: string, args: string[]): string {
  const result = spawnSync(command, args, {
    encoding: 'utf8',
    maxBuffer: 20 * 1024 * 1024,
  });

  if (result.error) {
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      `Failed to run ${command}: ${result.error.message}`,
      500,
    );
  }

  if (result.status !== 0) {
    const stderr = (result.stderr || '').trim();
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      stderr ? `${command} failed: ${stderr}` : `${command} exited with code ${result.status}`,
      500,
    );
  }

  return result.stdout ?? '';
}

function environmentValue(environment: NodeJS.ProcessEnv, key: string): string | undefined {
  return Object.entries(environment).find(
    ([name]) => name.toLowerCase() === key.toLowerCase(),
  )?.[1];
}

export function detectLocalTool(
  name: ToolName,
  options: {
    environment?: NodeJS.ProcessEnv;
    platform?: NodeJS.Platform;
  } = {},
): LocalToolAvailability {
  const environment = options.environment ?? process.env;
  const platform = options.platform ?? process.platform;
  const pathValue = environmentValue(environment, 'PATH') ?? '';
  const separator = platform === 'win32' ? ';' : ':';
  const extensions =
    platform === 'win32' && extname(name) === ''
      ? (environmentValue(environment, 'PATHEXT') ?? '.COM;.EXE;.BAT;.CMD')
          .split(';')
          .filter(Boolean)
      : [''];

  for (const entry of pathValue.split(separator)) {
    if (!entry) continue;
    const directory = entry.replace(/^"(.*)"$/, '$1');
    for (const extension of extensions) {
      const candidate = resolve(directory, `${name}${extension}`);
      try {
        accessSync(candidate, constants.X_OK);
        return { available: true, path: candidate };
      } catch {
        // Keep searching the remaining PATH entries.
      }
    }
  }

  return {
    available: false,
    path: null,
  };
}

function splitPages(raw: string, pageCount: number): string[] {
  const pages = raw.split('\f').map((value) => value.replace(/\r/g, '').trimEnd());

  while (pages.length > 0 && pages[pages.length - 1]?.trim() === '') {
    pages.pop();
  }

  if (pages.length < pageCount) {
    return [...pages, ...new Array(pageCount - pages.length).fill('')];
  }

  return pages.slice(0, pageCount);
}

function normalizeWhitespace(text: string): string {
  return text
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function needsOcr(text: string): boolean {
  const normalized = normalizeWhitespace(text);
  if (!normalized) return true;
  const alphaNumeric = (normalized.match(/[A-Za-z0-9]/g) || []).length;
  return normalized.length < 80 || alphaNumeric / normalized.length < 0.35;
}

function extractPageCount(sourcePath: string): number {
  const output = runCommand('pdfinfo', [sourcePath]);
  const match = output.match(/^Pages:\s+(\d+)$/m);
  if (!match) {
    throw new OkraRuntimeError(
      'INVALID_RESPONSE',
      `Unable to determine page count for ${sourcePath}`,
      500,
    );
  }
  return Number.parseInt(match[1]!, 10);
}

function ocrPage(sourcePath: string, pageNumber: number): string {
  const tempDir = mkdtempSync(join(tmpdir(), 'okra-local-ocr-'));
  const prefix = join(tempDir, `page-${pageNumber}`);
  try {
    runCommand('pdftoppm', [
      '-f',
      String(pageNumber),
      '-l',
      String(pageNumber),
      '-png',
      sourcePath,
      prefix,
    ]);
    // pdftoppm zero-pads the page suffix based on the document's page count
    // (e.g. page-09.png in a 59-page doc), so resolve whatever PNG it actually
    // wrote rather than guessing the exact filename.
    const produced = readdirSync(tempDir)
      .filter((name) => name.endsWith('.png'))
      .sort();
    if (produced.length === 0) {
      throw new OkraRuntimeError(
        'INVALID_RESPONSE',
        `pdftoppm did not produce an image for page ${pageNumber}`,
        500,
      );
    }
    const imagePath = join(tempDir, produced[0]!);
    return normalizeWhitespace(runCommand('tesseract', [imagePath, 'stdout', '--psm', '6']));
  } finally {
    rmSync(tempDir, { recursive: true, force: true });
  }
}

export function localToolReportEffect(
  dataDir: string,
): Effect.Effect<LocalDoctorReport, LocalExtractError> {
  return localExtractBoundary('localToolReport', () => localToolReportSync(dataDir));
}

export function localToolReport(dataDir: string): LocalDoctorReport {
  return runLocalExtractEffect(localToolReportEffect(dataDir));
}

function localToolReportSync(dataDir: string): LocalDoctorReport {
  const tools = {
    pdftotext: detectLocalTool('pdftotext'),
    pdfinfo: detectLocalTool('pdfinfo'),
    pdftoppm: detectLocalTool('pdftoppm'),
    tesseract: detectLocalTool('tesseract'),
  };

  return {
    ok: tools.pdftotext.available && tools.pdfinfo.available,
    dataDir,
    tools,
  };
}

export function extractLocalDocumentEffect(
  sourcePath: string,
  dataDir: string,
): Effect.Effect<ExtractedDocument, LocalExtractError> {
  return Effect.gen(function* () {
    const report = yield* localToolReportEffect(dataDir).pipe(
      Effect.mapError(
        (error) =>
          new LocalExtractError({
            method: 'extractLocalDocument',
            ...(error.status === undefined ? {} : { status: error.status }),
            cause: error.cause,
          }),
      ),
    );
    return yield* localExtractBoundary('extractLocalDocument', () =>
      extractLocalDocumentSync(sourcePath, report),
    );
  });
}

export function extractLocalDocument(sourcePath: string, dataDir: string): ExtractedDocument {
  return runLocalExtractEffect(extractLocalDocumentEffect(sourcePath, dataDir));
}

function extractLocalDocumentSync(
  sourcePath: string,
  report: LocalDoctorReport,
): ExtractedDocument {
  if (!report.tools.pdftotext.available || !report.tools.pdfinfo.available) {
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      'Local PDF extraction requires pdftotext and pdfinfo to be installed',
      500,
      report.tools,
    );
  }

  const pageCount = extractPageCount(sourcePath);
  const raw = runCommand('pdftotext', ['-layout', '-enc', 'UTF-8', sourcePath, '-']);
  const split = splitPages(raw, pageCount);

  let charCount = 0;
  let ocrUsed = false;
  const pages = split.map((pageText, index) => {
    const pageNumber = index + 1;
    let text = normalizeWhitespace(pageText);
    let ocrApplied = false;

    if (needsOcr(text) && report.tools.pdftoppm.available && report.tools.tesseract.available) {
      try {
        const ocrText = ocrPage(sourcePath, pageNumber);
        if (ocrText.length > text.length) {
          text = ocrText;
        }
        ocrApplied = ocrText.length > 0;
        ocrUsed = ocrUsed || ocrApplied;
      } catch {
        // Per-page OCR failure is non-fatal: keep the text-layer result for this
        // page rather than aborting extraction of the whole document.
      }
    }

    charCount += text.length;
    return { pageNumber, text, ocrApplied };
  });

  return {
    pageCount,
    charCount,
    ocrUsed,
    pages,
    tools: {
      ...report.tools,
      tesseract: report.tools.tesseract,
      pdftoppm: report.tools.pdftoppm,
      pdftotext: report.tools.pdftotext,
      pdfinfo: report.tools.pdfinfo,
    },
  };
}
